import { RULES_VERSION, type RunInputEvent } from './clipperRunReplay';

export interface RunAdmission { id: string; seed: number; rulesVersion: number; personalBest: number }
export interface OwnedRunAdmission extends RunAdmission { identity: string }
export interface RunRecording { frames: number; events: RunInputEvent[] }
export interface FinishRunInput extends RunRecording { runId: string }
export interface RunScoreTransport {
  start(): Promise<RunAdmission>;
  finish(input: FinishRunInput, keepalive: boolean): Promise<{ score: number; personalBest: number }>;
}
export type RunSaveStatus = 'local' | 'saving' | 'saved';

/** Network lifecycle, independent of rendering. An admission never changes a running game's seed. */
export function createRunScoreRecorder(options: {
  transport: RunScoreTransport;
  identity: () => string | null;
  onBest: (best: number) => void;
  onStatus: (status: RunSaveStatus) => void;
}) {
  let prepared: OwnedRunAdmission | null = null;
  let pending: Promise<void> | null = null;
  let disposed = false;
  // Only the current/most recently submitted game is retained by this controller.
  let generation = 0;
  let submittedId: string | null = null;
  let submission: Promise<void> | undefined;
  const current = (identity: string) => !disposed && identity === options.identity();
  return {
    prepare(): Promise<void> {
      const identity = options.identity();
      if (!identity || disposed || prepared?.identity === identity) return Promise.resolve();
      if (pending) return pending;
      prepared = null;
      pending = (async () => {
        try {
          const admission = await options.transport.start();
          if (current(identity) && admission.rulesVersion === RULES_VERSION
            && typeof admission.id === 'string' && admission.id.length > 0
            && Number.isInteger(admission.seed) && admission.seed > 0 && admission.seed <= 2147483647
            && Number.isInteger(admission.personalBest) && admission.personalBest >= 0) {
            prepared = { ...admission, identity };
            options.onBest(admission.personalBest);
          }
        } catch {
          // Failed/older APIs keep the game local and never stop input handling.
        } finally { pending = null; }
      })();
      return pending;
    },
    take(): OwnedRunAdmission | null {
      const admission = prepared;
      prepared = null;
      generation += 1;
      return admission && current(admission.identity) ? admission : null;
    },
    complete(admission: OwnedRunAdmission | null, recording: RunRecording, keepalive = false): Promise<void> {
      if (!admission || recording.frames < 1 || !current(admission.identity)) return Promise.resolve();
      if (submittedId === admission.id) return submission ?? Promise.resolve();
      const input = { runId: admission.id, frames: recording.frames, events: recording.events.map((event) => ({ ...event })) };
      submittedId = admission.id;
      const submittedGeneration = generation;
      const isLatest = () => generation === submittedGeneration && submittedId === admission.id;
      options.onStatus('saving');
      submission = (async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const result = await options.transport.finish(input, keepalive);
            if (current(admission.identity)) {
              options.onBest(result.personalBest);
              if (isLatest()) options.onStatus('saved');
            }
            return;
          } catch {
            if (attempt === 1 || keepalive || !current(admission.identity)) {
              if (current(admission.identity) && isLatest()) options.onStatus('local');
              return;
            }
            await new Promise((resolve) => setTimeout(resolve, 250));
            if (!current(admission.identity)) return;
          }
        }
      })();
      return submission;
    },
    dispose() { disposed = true; prepared = null; },
  };
}
